import { ReactElement, useEffect, useMemo, useRef, useImperativeHandle, forwardRef } from 'react'
import { useParams } from 'react-router-dom'
import { mapValueKey } from '@editorUtils'
import { AxiosError } from 'axios'
import { isUndefined, last, reduce } from 'lodash-es'
import { Calculate, Close } from '@mui/icons-material'
import { Box, IconButton, Stack } from '@mui/material'
import { GridRowsProp } from '@mui/x-data-grid-premium'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  subscribeCornerConnectionProposalEvent,
  unsubscribeCornerConnectionProposalEvent,
} from '@ui/events/cornerConnectionProposal'
import { Info } from '@ui/feedback'
import { Form } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import BottomDrawer from '@ui/structure/BottomDrawer'
import { useControlStore, useResultsStore } from '@editorStores'
import { useResultsSelection } from '@editorHooks'
import { getProjectConnectionPrioritization } from '@queries'
import { formulaToNumber } from '@utils'
import { saveManualLoadsPerSupport } from '@mutations'
import { buildErrorMessage } from 'src/constants/errors'
import useResultsQueries from '../../hooks/useResultsQueries'
import AnchorDataGridTable, { AnchorDataGrid2Ref } from './components/AnchorDataGridTable'
import AnchorGridDirtyBlocker from './components/AnchorGridDirtyBlocker'
import { anchorDataGridSchema } from './schema'
import useAnchorGridCloseGuard from './useAnchorGridCloseGuard'

interface Props {
  disallowSelection: boolean
  disableAllExcept?: { anchorGuid: string; fields: string[] }
  selectedConnector?: string | null
  onSelectConnector: (connector?: string) => void
}

const AnchorDataGrid2 = forwardRef<AnchorDataGrid2Ref | null, Props>(
  (
    { disallowSelection, disableAllExcept, selectedConnector, onSelectConnector }: Props,
    ref,
  ): ReactElement => {
    const { projectId } = useParams()
    const { enqueueSnackbar } = useSnackbar()
    const queryClient = useQueryClient()

    const anchorInterventionTableData = useResultsStore(state => state.anchorInterventionTableData)
    const anchorChecks = useResultsStore(state => state.anchorChecks)

    const { computeAnchorChecks, isComputingAnchorChecks } = useResultsQueries(projectId as string)

    const elementGuidToAnchorCheck = useMemo(
      () => (anchorChecks ? mapValueKey(anchorChecks, 'element_guid') : {}),
      [anchorChecks],
    )

    const supportGuidToAnchorName = useMemo(
      () =>
        reduce(
          anchorInterventionTableData,
          (col, el) => ({
            ...col,
            [el.support_guid]: elementGuidToAnchorCheck[el.support_guid]?.used_anchor.name,
          }),
          {} as Record<string, string>,
        ),
      [anchorInterventionTableData, elementGuidToAnchorCheck],
    )

    const defaultValues: { rows: GridRowsProp<AnchorInterventionsTableRowData> } | undefined =
      useMemo(() => {
        const rows = anchorInterventionTableData?.map(row => {
          const manualLoad1 = row.manual_loads[0]
          const manualLoad2 = row.manual_loads[1]
          const manualLoad3 = row.manual_loads[2]

          return {
            id: row.support_guid,
            ...row,
            anchor_name: supportGuidToAnchorName[row.support_guid],
            anchor_utilization: elementGuidToAnchorCheck[row.support_guid]?.utilization.toString(),
            manual_load_1: manualLoad1 ? manualLoad1?.load_formula : undefined,
            manual_source_1: manualLoad1?.source,
            manual_load_2: manualLoad2 ? manualLoad2?.load_formula : undefined,
            manual_source_2: manualLoad2?.source,
            manual_load_3: manualLoad3 ? manualLoad3?.load_formula : undefined,
            manual_source_3: manualLoad3?.source,
          }
        })

        if (!rows) return

        return { rows }
      }, [anchorInterventionTableData, elementGuidToAnchorCheck, supportGuidToAnchorName])

    const formRef = useRef<HTMLFormElement | null>(null)

    const { isLoading, mutateAsync } = useMutation(
      (requestData: ManualLoadOnSupport[]) => {
        return saveManualLoadsPerSupport.request(projectId, requestData)
      },
      {
        onSuccess: async () => {
          computeAnchorChecks(projectId as string)
          await queryClient.invalidateQueries(
            getProjectConnectionPrioritization.getKey(projectId as string),
          )
          enqueueSnackbar('Erfolgreich gespeichert', { variant: 'success' })
        },
        onError: (error: AxiosError) => {
          enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern'), { variant: 'error' })
        },
      },
    )

    const isDrawerExpanded = useControlStore(state => state.isDrawerExpanded)
    const isBottomDrawerExpanded = useControlStore(state => state.isBottomDrawerExpanded)
    const setIsBottomDrawerExpanded = useControlStore(state => state.setIsBottomDrawerExpanded)

    const { activeMode: activeResultsMode, setResultsMode } = useResultsSelection()

    const isOpen = activeResultsMode === 'tensile-connectors-table'

    const guardAnchorGridClose = useAnchorGridCloseGuard()

    const closeAnchorTable = () => {
      if (guardAnchorGridClose()) return
      setResultsMode(null)
    }

    const anchorTable2Ref = useRef<AnchorDataGrid2Ref>(null)

    // @ts-ignore
    useImperativeHandle(ref, () => anchorTable2Ref.current, [anchorTable2Ref])

    useEffect(() => {
      const handler = (event: CustomEvent) => {
        const detail = event.detail as {
          anchorGuid: string
          manualLoadField: string
          rawFormula: string
          maxForce: number
          value?: string
        }

        if (!detail?.anchorGuid || !detail?.manualLoadField) return

        const manualSourceField = `manual_source_${last(detail.manualLoadField)}`

        // If table is currently mounted, update immediately.
        anchorTable2Ref.current?.updateCornerConnectionState(
          detail.anchorGuid,
          detail.manualLoadField,
          manualSourceField,
          detail.rawFormula,
          detail.maxForce,
        )
      }

      subscribeCornerConnectionProposalEvent(handler as never)
      return () => unsubscribeCornerConnectionProposalEvent(handler as never)
    }, [anchorInterventionTableData, isOpen])

    return (
      <BottomDrawer
        isOpen={isOpen}
        setIsOpen={(isOpen: boolean) => {
          if (!isOpen) {
            closeAnchorTable()
            return
          }
          setResultsMode('tensile-connectors-table')
        }}
        expanded={isBottomDrawerExpanded}
        setIsDrawerExpanded={setIsBottomDrawerExpanded}
        sideDrawerExpanded={isDrawerExpanded}
        isLoading={isLoading || isComputingAnchorChecks}
        leftOffsetPx={22}
      >
        {isUndefined(defaultValues) ? (
          <Stack direction="column" display="flex" flex={1}>
            <Box display="flex" justifyContent="flex-end" p={0.5}>
              <IconButton onClick={closeAnchorTable}>
                <Close fontSize="small" />
              </IconButton>
            </Box>
            <Box display="flex" flex={1} justifyContent="center" alignItems="center">
              <Box>
                <Info icon={<Calculate />}>Daten noch nicht verfügbar</Info>
              </Box>
            </Box>
          </Stack>
        ) : (
          <Form
            ref={formRef}
            validationSchema={anchorDataGridSchema}
            resetIsDirtyAfterSubmit
            onSubmit={(
              state: { rows: GridRowsProp<AnchorInterventionsTableRowData> },
              _,
              isDirty,
            ) => {
              if (!isDirty) return

              const requestData: ManualLoadOnSupport[] = state.rows.map(row => {
                const manualLoads: ManualLoad[] = []

                if (row.manual_load_1 && row.manual_source_1) {
                  manualLoads.push({
                    load: formulaToNumber(row.manual_load_1.toString()) * 1000,
                    load_formula: `${row.manual_load_1}`,
                    source: row.manual_source_1,
                  })
                }
                if (row.manual_load_2 && row.manual_source_2) {
                  manualLoads.push({
                    load: formulaToNumber(row.manual_load_2.toString()) * 1000,
                    load_formula: `${row.manual_load_2}`,
                    source: row.manual_source_2,
                  })
                }
                if (row.manual_load_3 && row.manual_source_3) {
                  manualLoads.push({
                    load: formulaToNumber(row.manual_load_3.toString()) * 1000,
                    load_formula: `${row.manual_load_3}`,
                    source: row.manual_source_3,
                  })
                }

                return {
                  support_id: row.support_guid,
                  manual_loads: manualLoads,
                  comment: row.comment,
                  // This table does not edit the Zuganker; keep what is saved.
                  connectorSelectionPolicy: row.connectorSelectionPolicy,
                  userDefinedConnectorId: row.userDefinedConnectorId,
                }
              })

              mutateAsync(requestData)
            }}
            defaultValues={defaultValues}
            style={{ display: 'flex', flex: 1, height: '100%' }}
            enableReinitialize
          >
            <AnchorGridDirtyBlocker />
            <AnchorDataGridTable
              ref={anchorTable2Ref}
              disallowSelection={disallowSelection}
              onClose={closeAnchorTable}
              onSave={() => formRef.current?.requestSubmit()}
              disableAllExcept={disableAllExcept}
              selectedConnector={selectedConnector}
              onSelectConnector={onSelectConnector}
            />
          </Form>
        )}
      </BottomDrawer>
    )
  },
)

AnchorDataGrid2.displayName = 'AnchorDataGrid2'

export default AnchorDataGrid2
